import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Mail, Edit, UserX, UserCheck, ChevronLeft, ChevronRight, KeyRound, Loader, Download, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);

  const [exporting, setExporting] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ email: '', role: '', email_verified: false });
  const [editSaving, setEditSaving] = useState(false);

  // Deactivate dialog
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateUser, setDeactivateUser] = useState(null);

  // Action choice dialog
  const [actionOpen, setActionOpen] = useState(false);
  const [actionUser, setActionUser] = useState(null);

  // Delete confirmation dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Password reset dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      params.append('page', page);
      params.append('per_page', perPage);

      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page, perPage]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setPage(1);
    }, 300));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);

      const response = await axios.get(
        `${API}/admin/users/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `benutzer_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Export erfolgreich');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Exportieren');
    } finally {
      setExporting(false);
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      email: user.email || '',
      role: user.role || 'customer',
      email_verified: user.email_verified || false,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editUser) return;
    try {
      setEditSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/user/${editUser.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Benutzer aktualisiert');
      setEditOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateUser) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/user/${deactivateUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Benutzerkonto deaktiviert');
      setDeactivateOpen(false);
      setActionOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Deaktivieren');
    }
  };

  const handleDelete = async () => {
    if (!actionUser) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/user/${actionUser.id}/delete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Benutzerkonto endgültig gelöscht');
      setDeleteOpen(false);
      setActionOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  const handleReactivate = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/user/${userId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Benutzerkonto reaktiviert');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Reaktivieren');
    }
  };

  const handlePasswordReset = async () => {
    if (!resetUser) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/user/${resetUser.id}/password-reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Passwort-Reset E-Mail an ${resetUser.email} gesendet`);
      setResetOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { text: 'Admin', color: 'bg-purple-100 text-purple-800' },
      provider: { text: 'Dienstleister', color: 'bg-[#FFF0D1] text-[#2B2142]' },
      customer: { text: 'Kunde', color: 'bg-green-100 text-green-800' },
    };
    const badge = badges[role] || { text: role, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Suche nach E-Mail..."
            value={search}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B2142]"
          />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Rolle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Rollen</SelectItem>
            <SelectItem value="provider">Dienstleister</SelectItem>
            <SelectItem value="customer">Kunden</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-[#2B2142] text-white rounded-lg hover:bg-[#1E1730] disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exportiert...' : 'CSV Export'}
        </button>
      </div>

      <div className="text-sm text-gray-600">
        {total} Benutzer gefunden
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-[#2B2142]" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">E-Mail</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Rolle</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Verifiziert</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Erstellt</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.email}</td>
                      <td className="px-6 py-4">{getRoleBadge(u.role)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.company_name || u.customer_name || '-'}
                      </td>
                      <td className="px-6 py-4">
                        {u.email_verified ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Ja</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">Nein</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {u.is_active === false ? (
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">Deaktiviert</span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Aktiv</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEdit(u)}
                            className="p-2 hover:bg-[#FFF0D1]/30 rounded-lg text-[#2B2142]"
                            title="Bearbeiten"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setResetUser(u); setResetOpen(true); }}
                            className="p-2 hover:bg-purple-50 rounded-lg text-purple-600"
                            title="Passwort zurücksetzen"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          {u.is_active === false && (
                            <button
                              onClick={() => handleReactivate(u.id)}
                              className="p-2 hover:bg-green-50 rounded-lg text-green-600"
                              title="Reaktivieren"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => { setActionUser(u); setActionOpen(true); }}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                            title="Deaktivieren / Löschen"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="p-12 text-center">
                <p className="text-gray-600">Keine Benutzer gefunden</p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Seite {page} von {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>E-Mail, Rolle und Verifizierungsstatus des Benutzers ändern.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">E-Mail</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B2142]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Rolle</label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm(prev => ({ ...prev, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Kunde</SelectItem>
                  <SelectItem value="provider">Dienstleister</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="email_verified"
                checked={editForm.email_verified}
                onChange={(e) => setEditForm(prev => ({ ...prev, email_verified: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="email_verified" className="text-sm text-gray-700">E-Mail verifiziert</label>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setEditOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              onClick={saveEdit}
              disabled={editSaving}
              className="px-4 py-2 bg-[#2B2142] text-white rounded-lg hover:bg-[#1E1730] disabled:opacity-50"
            >
              {editSaving ? 'Speichert...' : 'Speichern'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation */}
      <AlertDialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzerkonto deaktivieren</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Konto von <strong>{deactivateUser?.email}</strong> wirklich deaktivieren?
              {deactivateUser?.role === 'provider' && ' Das Dienstleister-Profil wird ebenfalls ausgeblendet.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-red-600 hover:bg-red-700">
              Deaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Action Choice Dialog */}
      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzeraktion wählen</DialogTitle>
            <DialogDescription>
              Wählen Sie eine Aktion für <strong>{actionUser?.email}</strong>
              {actionUser?.role === 'provider' && ' (Dienstleister)'}
              {actionUser?.role === 'customer' && ' (Kunde)'}
              {actionUser?.role === 'admin' && ' (Admin)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <button
              onClick={() => {
                setDeactivateUser(actionUser);
                setDeactivateOpen(true);
              }}
              disabled={actionUser?.is_active === false}
              className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
            >
              <UserX className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-gray-900">Deaktivieren</p>
                <p className="text-sm text-gray-500">
                  Konto wird deaktiviert, Daten bleiben erhalten.
                  {actionUser?.is_active === false && ' (Bereits deaktiviert)'}
                </p>
              </div>
            </button>
            <button
              onClick={() => setDeleteOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors text-left"
            >
              <Trash2 className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-700">Endgültig löschen</p>
                <p className="text-sm text-gray-500">Konto und alle Daten werden unwiderruflich gelöscht.</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzerkonto endgültig löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Konto von <strong>{actionUser?.email}</strong> wirklich endgültig löschen?
              {' '}Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten des Benutzers werden unwiderruflich entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Löscht...' : 'Endgültig löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Confirmation */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Passwort zurücksetzen</AlertDialogTitle>
            <AlertDialogDescription>
              Eine E-Mail zum Zurücksetzen des Passworts wird an <strong>{resetUser?.email}</strong> gesendet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handlePasswordReset}>
              E-Mail senden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
